import { GameEvents } from '../GameEvents';
import type EventBus from '../EventBus';
import getLogger from '../logger';

const logger = getLogger('ProgressHandler');

export default class ProgressHandler {
  private state: any;
  private slice: any;
  private eventBus: EventBus;
  private progressManager: any;

  constructor(state: any, slice: any, eventBus: EventBus, progressManager: any) {
    this.state = state;
    this.slice = slice;
    this.eventBus = eventBus;
    this.progressManager = progressManager;

    // LEVEL_WON – does NOT consume
    this.eventBus.on(GameEvents.LEVEL_WON, (payload: any) => this._onLevelWon(payload));
    // LEVEL_LOST – does NOT consume
    this.eventBus.on(GameEvents.LEVEL_LOST, () => this._onLevelLost());
    this.eventBus.on(GameEvents.CLEAR_PROGRESS, () => this._onClearProgress());
    this.eventBus.on(GameEvents.SET_TUTORIAL_SHOWN, (payload: any) => this._onSetTutorialShown(payload));
    this.eventBus.on(GameEvents.SET_VISION_VIEWED, (payload: any) => this._onSetVisionViewed(payload));
    this.eventBus.on(GameEvents.CLAIM_REWARD, (payload: any) => this._onClaimReward(payload));
  }

  /**
   * Called by GameManager.init() after progressManager.init() completes.
   */
  init() {
    this._recomputeDerived();
  }

  async _onLevelWon(payload?: { moveCount: number; undoCount?: number; playerSolution?: any[] }) {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;

    await this.progressManager.updateBestSolution(levelKey, payload?.moveCount ?? 0, payload?.undoCount ?? 0, payload?.playerSolution);
    await this.progressManager.resetFailureCount(levelKey);
    this._recomputeDerived();
  }

  async _onLevelLost() {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;

    await this.progressManager.incrementFailureCount(levelKey);

    // Check if we just crossed the tutorial threshold – reset tutorialShown so it auto-shows on next load
    const tutorial = this.state.tutorial.availableTutorial;
    if (tutorial) {
      const levelState = this.progressManager.getLevelState(levelKey);
      const threshold = tutorial.failures || 0;
      if (levelState.failures === threshold) {
        await this.progressManager.setTutorialShown(levelKey, false);
      }
    }

    this._recomputeDerived();
  }

  async _onClearProgress() {
    await this.progressManager.clearProgress();
    this._recomputeDerived();
  }

  async _onSetTutorialShown({ levelKey }: { levelKey: string }) {
    await this.progressManager.setTutorialShown(levelKey, true);
    this._recomputeDerived();
  }

  async _onSetVisionViewed({ levelKey }: { levelKey: string }) {
    await this.progressManager.setVisionViewed(levelKey);
    this._recomputeDerived();
  }

  async _onClaimReward({ campaignId }: { campaignId: string }) {
    await this.progressManager.markRewardClaimed(campaignId);
    this._recomputeDerived();
  }

  _recomputeDerived() {
    // Sync levelStates from ProgressManager
    const rawProgress = this.progressManager.getCurrentProgress();
    this.slice.levelStates = { ...rawProgress.levelState };
    this.slice.claimedRewards = { ...rawProgress.claimedRewards };

    // Derived booleans
    this.slice.hasAnyProgress = this.progressManager.hasAnyProgress();
    this.slice.gameComplete = this.progressManager.isGameComplete();
    this.slice.nextUnbeatenLevel = this.progressManager.getNextUnbeatenLevel();

    // Derived maps (Record<string, boolean> for Vue reactivity)
    const { getAllLevelIds, getCampaignForLevel } = require('../levelManifest');
    const allIds: string[] = getAllLevelIds();
    const unlocked: Record<string, boolean> = {};
    const completed: Record<string, boolean> = {};
    for (const id of allIds) {
      unlocked[id] = this.progressManager.isLevelUnlocked(id);
      completed[id] = this.progressManager.isLevelComplete(id);
    }
    this.slice.unlockedLevelIds = unlocked;
    this.slice.completedLevelIds = completed;

    // Derived: does the current level's campaign have a claimable reward?
    this.slice.completedChapterReward = this._computeCompletedChapterReward(completed, getCampaignForLevel);
  }

  _computeCompletedChapterReward(
    completed: Record<string, boolean>,
    getCampaignForLevel: (id: string) => any,
  ) {
    const currentLevelKey = this.state.gameplay?.currentLevelKey;
    if (!currentLevelKey) return null;

    const campaign = getCampaignForLevel(currentLevelKey);
    if (!campaign?.reward) return null;

    // Already claimed?
    if (this.progressManager.isRewardClaimed(campaign.id)) return null;

    // All battles complete?
    const allComplete = campaign.levelIds.every((id: string) => completed[id]);
    if (!allComplete) return null;

    return {
      reward: campaign.reward,
      campaignId: campaign.id,
      campaignName: campaign.name,
    };
  }
}
